import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { isClerkEnabled } from './config/clerk';
import { identifyUser } from './services/analytics';
import { getOrCreateDeviceId } from './services/deviceIdentity';
import ClerkSessionBridge from './components/auth/ClerkSessionBridge';

// Keep layout components eager loaded
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Lazy load all page components
const Welcome = lazy(() => import('./pages/Welcome'));
const Home = lazy(() => import('./pages/Home'));
const Catalog = lazy(() => import('./pages/Catalog'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Payment = lazy(() => import('./pages/Payment'));
const Invoice = lazy(() => import('./pages/Invoice'));
const ProofUpload = lazy(() => import('./pages/ProofUpload'));
const DeliveryForm = lazy(() => import('./pages/DeliveryForm'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const Login = lazy(() => import('./pages/Login'));
const ClerkAuthCallback = lazy(() => import('./pages/ClerkAuthCallback'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function Shell() {
  const location = useLocation();
  const isWelcome = location.pathname === '/';
  
  // Identify anonymous user with device fingerprint
  useEffect(() => {
    const deviceId = getOrCreateDeviceId();
    
    // Identify user in PostHog
    identifyUser(deviceId, {
      device_id: deviceId,
      user_type: 'anonymous',
      created_at: new Date().toISOString(),
    });
  }, []);
  
  return (
    <>
      {isClerkEnabled ? <ClerkSessionBridge /> : null}
      <Navbar />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/home" element={<Home />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/upload-proof/:orderId" element={<ProofUpload />} />
          <Route path="/delivery" element={<DeliveryForm />} />
          <Route path="/confirmation/:orderId" element={<Confirmation />} />
          <Route path="/login" element={<Login />} />
          {isClerkEnabled ? <Route path="/sso-callback" element={<ClerkAuthCallback />} /> : null}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isWelcome && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Shell />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
